import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["switch", "label", "status", "messages"];
  static values  = { affiliateId: Number, required: Boolean };

  connect() {
    this.switchTarget.checked = this.requiredValue;
    this.renderState(this.requiredValue, /*pending*/ false);
  }

  async commit(event) {
    event?.preventDefault();

    const id = this.affiliateIdValue;
    if (!id) return this.inlineNote("Missing affiliate id.", "danger");

    const desired = this.switchTarget.checked;

    // Optimistic busy state
    this.switchTarget.disabled = true;
    this.inlineNote("Saving…");
    this.renderState(desired, /*pending*/ true);

    try {
      const res = await fetch(`/affiliates/${id}/update-mfa-requirement`, {
        method: "PATCH",
        headers: {
          "X-CSRF-Token": this.csrfToken,
          "Accept": "application/json",
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ mfa_required: desired })
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `Update failed (HTTP ${res.status}).`);

      const applied = !!data.required;
      this.requiredValue = applied;
      this.switchTarget.checked = applied;
      this.renderState(applied, /*pending*/ false);

      const msg = data.message || (applied ? "MFA enabled." : "MFA disabled.");
      this.toast(msg, "success");
      this.inlineNote(""); // clear inline note
    } catch (e) {
      // Revert to last known state
      this.switchTarget.checked = this.requiredValue;
      this.renderState(this.requiredValue, /*pending*/ false);
      this.toast(e.message || "Failed to update MFA.", "danger");
      this.inlineNote("Update failed.", "danger");
    } finally {
      this.switchTarget.disabled = false;
    }
  }

  renderState(required, pending) {
    // Badge/status
    if (this.hasStatusTarget) {
      if (pending) {
        this.statusTarget.innerHTML =
          `<span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
           <span>Saving…</span>`;
      } else {
        this.statusTarget.innerHTML = required
          ? '<span class="badge text-bg-success">MFA required</span>'
          : '<span class="badge text-bg-secondary">MFA not required</span>';
      }
    }

    // Optional label color cues
    if (this.hasLabelTarget) {
      this.labelTarget.classList.toggle("text-success", required);
      this.labelTarget.classList.toggle("text-muted", !required);
    }

    this.element.classList.toggle("border-success", required)
  }

  // --- feedback helpers ---

  toast(text, level = "info") {
    // Use Bootstrap toast if available; otherwise fall back to inline note
    const bs = window.bootstrap;
    if (!bs || !bs.Toast) return this.inlineNote(text, level);

    // Ensure a container exists (bottom-right)
    let container = document.getElementById("toaster");
    if (!container) {
      container = document.createElement("div");
      container.id = "toaster";
      container.style.position = "fixed";
      container.style.right = "1rem";
      container.style.bottom = "1rem";
      container.style.zIndex = "1080";
      document.body.appendChild(container);
    }

    const el = document.createElement("div");
    el.className = "toast align-items-center text-bg-" + this.toastLevel(level) + " border-0";
    el.setAttribute("role", "status");
    el.setAttribute("aria-live", "polite");
    el.setAttribute("aria-atomic", "true");
    el.innerHTML = `
      <div class="d-flex">
        <div class="toast-body">${this.escape(text)}</div>
        <button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast" aria-label="Close"></button>
      </div>
    `;
    container.appendChild(el);
    new bs.Toast(el, { delay: 2200 }).show();
  }

  inlineNote(text, level = "muted") {
    if (!this.hasMessagesTarget) return;
    const cls = level === "danger" ? "text-danger" : "text-muted";
    this.messagesTarget.className = cls;
    this.messagesTarget.textContent = text || "";
  }

  toastLevel(level) {
    // map to bootstrap contextual bg-* names
    switch (level) {
      case "success": return "success";
      case "danger":  return "danger";
      case "warning": return "warning";
      default:        return "secondary";
    }
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content;
  }
  escape(str) {
    const p = document.createElement("p");
    p.textContent = str ?? "";
    return p.innerHTML;
  }
}
